// ONE pager for every "load the whole list" read (#2761). Supabase silently caps any select at 1000 rows (a .limit(5000) is capped the same way), so an unpaged load of
// companies or contacts stops at row 1000 with no error and the list LOOKS complete: a company looks missing and gets created again. Every full-list read goes through here.
//
//   fetchAllRows(supabase, 'companies', { select: 'id, name', filter: (q) => q.is('deleted_at', null), order: ['name'], label: 'Scout companies' })
//   fetchAllByIds(supabase, 'contacts', { select: 'id, email', ids, column: 'id', label: 'Apollo finds' })
//
// Rules:
//   - STABLE order: the order you give, then `id` as the tiebreak (or `tiebreak: null` for a view with no id), so pages read at different moments never overlap or skip a row;
//   - pages of PAGE (1000); the read stops on the first SHORT page; an exact multiple of PAGE is fine (the next page comes back empty);
//   - a failed page THROWS, with the label and page number. A partial list is never returned, because a partial list looks complete;
//   - `parallelFirst` pages go out together (a cold load of four pages used to be four round trips); later pages follow in order;
//   - maxRows is a runaway guard: reaching it throws instead of returning a silent partial.
// warnIfCapped() is for the reads that stay a bounded search on purpose: in development it warns when a result length equals the cap it was asked for.
//
// This file is mirrored byte for byte at api/_lib/fetchAll.js (a test keeps them identical). It imports nothing, so it runs in the browser and in the serverless functions.

export const PAGE = 1000;
export const MAX_ROWS = 200000;

const asOrder = (o) => (typeof o === 'string' ? { column: o, ascending: true } : { ascending: true, ...o });

function pageQuery(supabase, table, { select, filter, order, tiebreak }, from, to) {
  let q = supabase.from(table).select(select);
  if (filter) q = filter(q);
  const cols = (order || []).map(asOrder);
  if (tiebreak && !cols.some((c) => c.column === tiebreak)) cols.push({ column: tiebreak, ascending: true });
  for (const c of cols) q = q.order(c.column, { ascending: c.ascending, ...(c.nullsFirst !== undefined ? { nullsFirst: c.nullsFirst } : {}) });
  return q.range(from, to);
}

export async function fetchAllRows(supabase, table, opts = {}) {
  const { select = '*', filter = null, order = [], tiebreak = 'id', page = PAGE, parallelFirst = 1, maxRows = MAX_ROWS, label = table } = opts;
  if (!supabase || !table) throw new Error('fetchAllRows needs a client and a table');
  if (!order.length && !tiebreak) throw new Error(`${label}: a paged read needs a stable order`);
  const cfg = { select, filter, order, tiebreak };
  const read = async (n) => {
    const from = n * page;
    const r = await pageQuery(supabase, table, cfg, from, from + page - 1);
    if (r && r.error) throw new Error(`${label}: page ${n + 1} failed (${r.error.message || r.error})`);
    return (r && r.data) || [];
  };
  const out = [];
  let n = 0;
  const k = Math.max(1, parallelFirst);
  if (k > 1) {
    const firsts = await Promise.all(Array.from({ length: k }, (_, i) => read(i)));
    for (const rows of firsts) {
      out.push(...rows);
      n += 1;
      if (rows.length < page) return out;
    }
  }
  for (;;) {
    if (out.length >= maxRows) throw new Error(`${label}: more than ${maxRows} rows; use a search or narrow the filter`);
    const rows = await read(n);
    out.push(...rows);
    n += 1;
    if (rows.length < page) return out;
  }
}

// A list of ids, in chunks (a long in() is cut off by the URL limit as well as the row cap). Each chunk is itself paged, so a chunk that matches more rows than ids
// (a company id list against contacts.company_id) is complete too.
export async function fetchAllByIds(supabase, table, opts = {}) {
  const { ids = [], column = 'id', chunk = 100, filter = null, label = table, ...rest } = opts;
  const unique = [...new Set((ids || []).filter((x) => x !== null && x !== undefined && x !== ''))];
  const out = [];
  for (let i = 0; i < unique.length; i += chunk) {
    const part = unique.slice(i, i + chunk);
    const rows = await fetchAllRows(supabase, table, { ...rest, label, filter: (q) => (filter ? filter(q.in(column, part)) : q.in(column, part)) });
    out.push(...rows);
  }
  return out;
}

// For a read that is a bounded search on purpose: a result exactly as long as the cap it asked for is probably cut off. Dev only unless `always` (server logs).
export function warnIfCapped(rows, { cap = PAGE, label = 'query', always = false } = {}) {
  const dev = typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';
  if (Array.isArray(rows) && rows.length === cap && (dev || always)) {
    // eslint-disable-next-line no-console
    console.warn(`[fetchAll] ${label} returned exactly ${cap} rows: it is probably cut off. Page it with fetchAllRows or make it a search.`);
    return true;
  }
  return false;
}
